import React from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Lock } from "lucide-react";
import Nav from "@/components/Nav";

/**
 * AidWaive tool shell, premium editorial chrome shared by every tool page.
 *
 * The wrapper renders:
 *   - global Nav (white, sticky)
 *   - editorial tool header: eyebrow, italic-blue accent on the last word
 *     of the title, subtitle, and a green "Nothing is saved" privacy pill
 *   - the page content in a constrained max-width column
 *   - a slim editorial footer
 *
 * Pages opt in to a wider max-width via `wide`. Pages that have their own
 * Nav (Landing, Journey, NetPrice, SideBySide) do NOT use ToolShell.
 *
 * Props:
 *   tool:            short label, e.g. "Tool 03 · SAI Estimator"
 *   title:           H1 string. The last word renders in italic blue
 *                    automatically. Pass a JSX node to opt out.
 *   subtitle:        one-line description under H1
 *   wide:            opt in to a wider container (Compare, Scholarships, Glossary)
 *   hidePrivacyBadge: true on glossary/legal/static pages
 */
export default function ToolShell({
  tool,
  title,
  subtitle,
  children,
  wide = false,
  hidePrivacyBadge = false,
}) {
  const max = wide ? "max-w-5xl" : "max-w-3xl";

  return (
    <div className="theme-light min-h-screen flex flex-col bg-white">
      <Nav />

      <main className="flex-1 bg-white">
        <div className={`${max} mx-auto w-full px-4 sm:px-6 py-10 sm:py-14`}>
          {/* Tool header */}
          <div className="mb-10">
            <Link
              to="/"
              className="inline-flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-900 transition-colors mb-7"
              data-testid="tool-back">
              <ArrowLeft className="w-3.5 h-3.5" />
              Back to all tools
            </Link>

            <div className="flex items-start justify-between gap-4 flex-wrap">
              <div className="min-w-0">
                {tool && (
                  <div className="text-[11px] uppercase tracking-[0.22em] text-gray-400 mb-3"
                    data-testid="tool-eyebrow">
                    {tool}
                  </div>
                )}
                <h1
                  className="font-heading text-gray-900 leading-[1.05]"
                  style={{ fontSize: "clamp(2.2rem, 4vw, 3.2rem)", letterSpacing: "-0.012em" }}>
                  {renderTitle(title)}
                </h1>
                {subtitle && (
                  <p className="mt-4 text-gray-500 text-base sm:text-lg leading-relaxed max-w-2xl">
                    {subtitle}
                  </p>
                )}
              </div>

              {!hidePrivacyBadge && (
                <span
                  className="shrink-0 inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-[11px] uppercase tracking-[0.16em]"
                  style={{ background: "#ecfdf5", color: "#15803d", border: "1px solid #a7f3d0" }}
                  data-testid="privacy-pill">
                  <Lock className="w-3 h-3" strokeWidth={2.2} />
                  Nothing is saved
                </span>
              )}
            </div>
          </div>

          {children}
        </div>
      </main>

      {/* Slim editorial footer, white. */}
      <footer className="border-t border-gray-200 bg-white">
        <div className={`${max} mx-auto w-full px-4 sm:px-6 py-8 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 text-xs text-gray-400`}
          data-testid="tool-footer">
          <Link to="/" className="hover:text-gray-900 transition-colors">
            ← All AidWaive tools
          </Link>
          <div className="flex gap-5">
            <Link to="/journey" className="hover:text-gray-900 transition-colors">The Journey</Link>
            <Link to="/glossary" className="hover:text-gray-900 transition-colors">Glossary</Link>
            <Link to="/legal/privacy" className="hover:text-gray-900 transition-colors">Privacy</Link>
            <Link to="/legal/terms" className="hover:text-gray-900 transition-colors">Terms</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}

/**
 * Render the title with the last word italicized in AidWaive blue, the
 * same accent treatment used on every landing-page section heading.
 * If the consumer passes JSX (not a string) it's rendered as-is.
 */
function renderTitle(title) {
  if (typeof title !== "string") return title;
  const trimmed = title.trim();
  // Find the last whitespace, accent everything from there to the end so
  // multi-word phrases like "in plain English." stay intact.
  const idx = trimmed.lastIndexOf(" ");
  if (idx < 0) {
    return <em className="aw-accent not-italic" style={{ fontStyle: "italic", color: "#1d4ed8" }}>{trimmed}</em>;
  }
  const head = trimmed.slice(0, idx);
  const tail = trimmed.slice(idx + 1);
  return (
    <>
      {head}{" "}
      <em style={{ fontStyle: "italic", color: "#1d4ed8" }}>{tail}</em>
    </>
  );
}
